import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { z } from "zod"
import { UNIT_STATUSES, type UnitStatus } from "@/inventory"
import { StatusChangeDialog } from "@/inventory/components/status-change-dialog"
import {
  UnitFilters,
  type UnitFilterValues,
} from "@/inventory/components/unit-filters"
import { UnitStatusBadge } from "@/inventory/components/unit-status-badge"
import {
  bulkUpdateUnitStatusFn,
  listCarriersFn,
  listProductsFn,
  listUnitsFn,
  listVendorsFn,
  updateUnitStatusFn,
} from "@/inventory/rpc"
import { Button } from "@/ui/button"
import { Checkbox } from "@/ui/checkbox"
import { cn } from "@/ui/utils"

const unitsSearchSchema = z.object({
  type: z.enum(["sim", "device"]).optional().catch(undefined),
  status: z.enum(UNIT_STATUSES).optional().catch(undefined),
  carrierId: z.coerce.number().int().positive().optional().catch(undefined),
  vendorId: z.coerce.number().int().positive().optional().catch(undefined),
  productId: z.coerce.number().int().positive().optional().catch(undefined),
  serialQuery: z.string().optional().catch(undefined),
  page: z.coerce.number().int().positive().optional().catch(undefined),
  pageSize: z.coerce.number().int().positive().optional().catch(undefined),
})

export type UnitsSearch = z.infer<typeof unitsSearchSchema>

export const Route = createFileRoute("/_inventory-layout/inventory/units")({
  validateSearch: (search) => unitsSearchSchema.parse(search),
  component: UnitsPage,
})

interface UnitListItem {
  id: number
  serialNumber: string
  status: UnitStatus
  product: { id: number; name: string; type: string } | null
  carrier: { id: number; name: string } | null
  receiveBatch: {
    id: number
    vendor: { id: number; name: string } | null
  } | null
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message
  }
  return "Something went wrong"
}

function stripUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const next: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined && value !== "") {
      next[key] = value
    }
  }
  return next as Partial<T>
}

function UnitsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? 50

  const filterValues: UnitFilterValues = {
    type: search.type,
    status: search.status,
    carrierId: search.carrierId,
    vendorId: search.vendorId,
    productId: search.productId,
    serialQuery: search.serialQuery,
  }

  const listParams = useMemo(
    () =>
      stripUndefined({
        type: search.type,
        status: search.status,
        carrierId: search.carrierId,
        vendorId: search.vendorId,
        productId: search.productId,
        serialQuery: search.serialQuery,
        page,
        pageSize,
      }),
    [search, page, pageSize],
  )

  const unitsQuery = useQuery({
    queryKey: ["units", listParams],
    queryFn: () => listUnitsFn({ data: listParams }),
  })

  const carriersQuery = useQuery({
    queryKey: ["carriers", { includeInactive: true }],
    queryFn: () => listCarriersFn({ data: { includeInactive: true } }),
  })

  const vendorsQuery = useQuery({
    queryKey: ["vendors", { includeInactive: true }],
    queryFn: () => listVendorsFn({ data: { includeInactive: true } }),
  })

  const productsQuery = useQuery({
    queryKey: ["products", { includeInactive: true }],
    queryFn: () => listProductsFn({ data: { includeInactive: true } }),
  })

  const items = (unitsQuery.data?.items ?? []) as UnitListItem[]
  const total = unitsQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [dialogMode, setDialogMode] = useState<
    null | { kind: "single"; unit: UnitListItem } | { kind: "bulk" }
  >(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const pageIds = items.map((item) => item.id)
  const allPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
  const somePageSelected = pageIds.some((id) => selectedIds.has(id))

  function setFilters(values: UnitFilterValues) {
    setSelectedIds(new Set())
    void navigate({
      search: stripUndefined({
        ...values,
        page: undefined,
        pageSize: search.pageSize,
      }),
    })
  }

  function setPage(nextPage: number) {
    setSelectedIds(new Set())
    void navigate({
      search: (prev) =>
        stripUndefined({
          ...prev,
          page: nextPage <= 1 ? undefined : nextPage,
        }),
    })
  }

  function toggleId(id: number, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  function toggleAllPage(checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of pageIds) {
        if (checked) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  const updateStatus = useMutation({
    mutationFn: (input: {
      unitId: number
      status: UnitStatus
      note: string | null
    }) =>
      updateUnitStatusFn({
        data: {
          unitId: input.unitId,
          status: input.status,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setDialogError(null)
      setDialogMode(null)
      await queryClient.invalidateQueries({ queryKey: ["units"] })
    },
    onError: (error) => setDialogError(getErrorMessage(error)),
  })

  const bulkUpdate = useMutation({
    mutationFn: (input: {
      unitIds: number[]
      status: UnitStatus
      note: string | null
    }) =>
      bulkUpdateUnitStatusFn({
        data: {
          unitIds: input.unitIds,
          status: input.status,
          note: input.note,
        },
      }),
    onSuccess: async () => {
      setDialogError(null)
      setDialogMode(null)
      setSelectedIds(new Set())
      await queryClient.invalidateQueries({ queryKey: ["units"] })
    },
    onError: (error) => setDialogError(getErrorMessage(error)),
  })

  const isSubmitting = updateStatus.isPending || bulkUpdate.isPending

  async function handleDialogSubmit(data: {
    status: UnitStatus
    note: string | null
  }) {
    if (!dialogMode) return
    setDialogError(null)
    try {
      if (dialogMode.kind === "single") {
        await updateStatus.mutateAsync({
          unitId: dialogMode.unit.id,
          status: data.status,
          note: data.note,
        })
      } else {
        await bulkUpdate.mutateAsync({
          unitIds: [...selectedIds],
          status: data.status,
          note: data.note,
        })
      }
    } catch {
      // surfaced via onError
    }
  }

  const listError = unitsQuery.isError
    ? getErrorMessage(unitsQuery.error)
    : null

  const selectedCount = selectedIds.size

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-semibold text-2xl tracking-tight">Units</h1>
        <p className="mt-1 text-muted-foreground text-sm">
          Filter inventory units and update status individually or in bulk.
        </p>
      </div>

      <UnitFilters
        values={filterValues}
        carriers={carriersQuery.data ?? []}
        vendors={vendorsQuery.data ?? []}
        products={productsQuery.data ?? []}
        onChange={setFilters}
      />

      {selectedCount > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-2">
          <span className="text-sm">{selectedCount} selected</span>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              setDialogError(null)
              setDialogMode({ kind: "bulk" })
            }}
          >
            Change status
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </Button>
        </div>
      ) : null}

      {listError ? (
        <p className="text-destructive text-sm" role="alert">
          {listError}
        </p>
      ) : null}

      {unitsQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No units match these filters.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    checked={
                      allPageSelected
                        ? true
                        : somePageSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(checked) =>
                      toggleAllPage(checked === true)
                    }
                    aria-label="Select all on page"
                  />
                </th>
                <th className="px-3 py-2 font-medium">Serial</th>
                <th className="px-3 py-2 font-medium">Product</th>
                <th className="px-3 py-2 font-medium">Carrier</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Vendor</th>
                <th className="px-3 py-2 font-medium">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((unit) => (
                <tr key={unit.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={selectedIds.has(unit.id)}
                      onCheckedChange={(checked) =>
                        toggleId(unit.id, checked === true)
                      }
                      aria-label={`Select ${unit.serialNumber}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium font-mono text-xs sm:text-sm">
                    <Link
                      to="/inventory/units/$unitId"
                      params={{ unitId: String(unit.id) }}
                      className="text-primary underline-offset-4 hover:underline"
                    >
                      {unit.serialNumber}
                    </Link>
                  </td>
                  <td className="px-3 py-2">
                    {unit.product?.name ?? "—"}
                    {unit.product?.type ? (
                      <span className="ml-1 text-muted-foreground capitalize">
                        ({unit.product.type})
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {unit.carrier?.name ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <UnitStatusBadge status={unit.status} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {unit.receiveBatch?.vendor?.name ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDialogError(null)
                          setDialogMode({ kind: "single", unit })
                        }}
                      >
                        Status
                      </Button>
                      <Button variant="ghost" size="sm" asChild>
                        <Link
                          to="/inventory/units/$unitId"
                          params={{ unitId: String(unit.id) }}
                        >
                          View
                        </Link>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-muted-foreground text-sm">
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1 || unitsQuery.isFetching}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span
              className={cn("min-w-[5rem] text-center text-sm tabular-nums")}
            >
              Page {page} / {totalPages}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= totalPages || unitsQuery.isFetching}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <StatusChangeDialog
        open={dialogMode != null}
        onOpenChange={(open) => {
          if (!open) {
            setDialogMode(null)
            setDialogError(null)
          }
        }}
        currentStatus={
          dialogMode?.kind === "single" ? dialogMode.unit.status : undefined
        }
        unitCount={dialogMode?.kind === "bulk" ? selectedCount : 1}
        isSubmitting={isSubmitting}
        error={dialogError}
        onSubmit={handleDialogSubmit}
      />
    </div>
  )
}
